import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatPrice, formatDate, formatDateTime, localized } from "@/lib/api";
import { Truck, PackageCheck, Clock, ChevronLeft, ExternalLink } from "lucide-react";

const STATUS_ICON = {
  pending: Clock,
  shipped: Truck,
  partial: Truck,
  delivered: PackageCheck,
};

// Carrier tracking URL templates so the tracking number becomes a working link.
const CARRIER_URLS = {
  UPS: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  FedEx: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  USPS: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  DHL: (n) => `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(n)}`,
};

const carrierTrackUrl = (carrier, number) => {
  if (!number) return null;
  const fn = CARRIER_URLS[carrier];
  return fn ? fn(number) : null;
};

// Map shipped_at -> expected delivery (5 working days as a coarse default).
const expectedDelivery = (shippedAt) => {
  if (!shippedAt) return null;
  const d = new Date(shippedAt);
  d.setDate(d.getDate() + 5);
  return d.toISOString();
};

export default function OrderTracking() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [vendors, setVendors] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${order_id}`);
        if (cancelled) return;
        setOrder(data);
        // Fetch vendor display names for items we don't have inline.
        const ids = Array.from(new Set(data.items.map((i) => i.vendor_id)));
        const fetched = {};
        await Promise.all(
          ids.map(async (vid) => {
            try {
              const { data: v } = await api.get(`/vendors/${vid}`);
              fetched[vid] = v;
            } catch {/* noop */}
          })
        );
        if (!cancelled) setVendors(fetched);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "generic");
      }
    })();
    return () => { cancelled = true; };
  }, [order_id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="order-tracking-error">
        <p className="overline">{t("tracking.overline")}</p>
        <h1 className="font-serif text-4xl mt-2">{t(error === "not_found" ? "tracking.notFound" : "errors.generic")}</h1>
        <Link to="/account" className="inline-block mt-8 bg-olive text-bone px-6 py-3 rounded-sm text-sm">{t("tracking.backToOrders")}</Link>
      </div>
    );
  }

  if (!order) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-ink-muted" data-testid="order-tracking-loading">{t("common.loading")}</div>;
  }

  const overall = order.fulfillment || "pending";
  const OverallIcon = STATUS_ICON[overall] || Clock;
  const displayLang = order.currency === "eur" ? "hr" : "en";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="order-tracking-page">
      <Link to="/account" className="overline inline-flex items-center gap-1 hover:text-olive" data-testid="tracking-back">
        <ChevronLeft className="w-3 h-3" />{t("tracking.backToOrders")}
      </Link>

      <div className="mt-6 flex items-start justify-between flex-wrap gap-6">
        <div>
          <p className="overline">{t("tracking.overline")}</p>
          <h1 className="font-serif text-5xl tracking-tight mt-2" data-testid="tracking-order-id">
            {t("tracking.order")} <span className="font-mono text-3xl text-ink-soft">{order.order_id.slice(-8)}</span>
          </h1>
          <p className="text-ink-soft mt-2 text-sm">{formatDateTime(order.created_at, lang)}</p>
        </div>
        <div className="text-right">
          <p className="overline">{t("account.total")}</p>
          <p className="font-medium text-2xl font-serif" data-testid="tracking-total">{formatPrice(order.amount, displayLang)}</p>
        </div>
      </div>

      {/* Big status banner */}
      <div
        className={
          "mt-10 rounded-sm hairline p-6 flex items-center gap-4 " +
          (overall === "delivered"
            ? "bg-olive/10 border-olive/30"
            : overall === "shipped" || overall === "partial"
              ? "bg-bone-alt"
              : "bg-boneAlt")
        }
        data-testid="tracking-status-banner"
      >
        <div className="w-12 h-12 rounded-full bg-olive text-bone flex items-center justify-center flex-shrink-0">
          <OverallIcon className="w-5 h-5" />
        </div>
        <div>
          <p className="font-serif text-2xl" data-testid="tracking-status-label">{t(`tracking.status.${overall}`)}</p>
          <p className="text-sm text-ink-soft mt-1">{t(`tracking.statusHint.${overall}`)}</p>
        </div>
      </div>

      {/* Per-vendor fulfillment cards */}
      <h2 className="font-serif text-2xl mt-12 mb-6">{t("tracking.bottlesAndShipments")}</h2>
      <div className="space-y-6" data-testid="tracking-vendor-cards">
        {Object.entries(
          order.items.reduce((acc, it) => {
            (acc[it.vendor_id] = acc[it.vendor_id] || []).push(it);
            return acc;
          }, {})
        ).map(([vendor_id, items]) => {
          const f = order.fulfillment_by_vendor?.[vendor_id] || { status: "pending" };
          const v = vendors[vendor_id];
          const VIcon = STATUS_ICON[f.status] || Clock;
          const trackUrl = carrierTrackUrl(f.carrier, f.tracking_number);
          const eta = expectedDelivery(f.shipped_at);
          return (
            <div key={vendor_id} className="bg-white hairline rounded-sm p-6" data-testid={`tracking-vendor-${vendor_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="overline">{t("tracking.shipmentFrom")}</p>
                  {v ? (
                    <Link to={`/vendor/${vendor_id}`} className="font-serif text-2xl hover:text-olive">{localized(v, "farm_name", lang)}</Link>
                  ) : (
                    <p className="font-serif text-2xl">{items[0]?.vendor_name || vendor_id}</p>
                  )}
                </div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-boneAlt text-xs uppercase tracking-widest" data-testid={`tracking-vendor-status-${vendor_id}`}>
                  <VIcon className="w-3 h-3" />{t(`tracking.status.${f.status}`)}
                </span>
              </div>

              <ul className="mt-4 space-y-2">
                {items.map((it, i) => (
                  <li key={i} className="flex items-center gap-4 text-sm py-2 border-t border-edge first:border-t-0">
                    {it.image && <img src={fileUrl(it.image)} alt={it.title} className="w-12 h-12 object-cover rounded-sm bg-boneAlt" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{it.title}</p>
                      <p className="text-ink-muted text-xs">{it.quantity} × {formatPrice(it.price, displayLang)}</p>
                    </div>
                    <span className="text-ink-soft">{formatPrice(it.price * it.quantity, displayLang)}</span>
                  </li>
                ))}
              </ul>

              {f.tracking_number && (
                <div className="mt-5 pt-4 border-t border-edge grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm" data-testid={`tracking-detail-${vendor_id}`}>
                  <div>
                    <p className="overline">{t("tracking.carrier")}</p>
                    <p className="mt-1">{f.carrier || "—"}</p>
                  </div>
                  <div>
                    <p className="overline">{t("tracking.number")}</p>
                    {trackUrl ? (
                      <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-olive hover:underline font-mono" data-testid={`tracking-number-link-${vendor_id}`}>
                        {f.tracking_number}<ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <p className="mt-1 font-mono">{f.tracking_number}</p>
                    )}
                  </div>
                  <div>
                    <p className="overline">{f.delivered_at ? t("tracking.deliveredOn") : t("tracking.expectedBy")}</p>
                    <p className="mt-1" data-testid={`tracking-eta-${vendor_id}`}>{formatDate(f.delivered_at || eta, lang)}</p>
                  </div>
                </div>
              )}
              {!f.tracking_number && f.status === "pending" && (
                <p className="mt-4 text-sm text-ink-muted italic">{t("tracking.awaitingShipment")}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
